import * as React from 'react';

import {
    readLocalStorage,
    writeLocalStorage,
    removeLocalStorageItem,
} from '../../../../util/localStorage';

import {useInitiateWhatsAppSync} from '../graphql/hooks/use-initiate-whatsapp-sync';

import {OnboardingModal} from './onboarding-modal';

type Props = {
    isConnected: boolean;
    isEnabled: boolean;
    enableIntegration: () => void;
    disableIntegration: () => void;
    refetchWhatsAppIntegration: () => Promise<any>;
    whatsappPhoneNumber: string | null | undefined;
    setIsOnboardingModalOpen: (isOnboardingModalOpen: boolean) => void;
    errorMessage: string | null | undefined;
    setErrorMessage: (errorMessage: string) => void;
};

const STORAGE_KEY_CODE = 'whatsapp_oauth_code';
const STORAGE_KEY_DATA = 'whatsapp_oauth_data';

// wraps the onboarding modal with all of the logic needed to actually launch
// the connection flow with whatsapp
export function OnboardingModalWrapper(props: Props) {
    const {refetchWhatsAppIntegration, setErrorMessage} = props;

    const [accessCode, setAccessCode] = React.useState<string | null>(null);
    const [whatsAppData, setWhatsAppData] = React.useState<any | null>(null);
    const [pageAutoAdvance, setPageAutoAdvance] = React.useState(false);

    const {initiateWhatsAppSync} = useInitiateWhatsAppSync();

    React.useEffect(() => {
        const storedCode = readLocalStorage(STORAGE_KEY_CODE);
        if (storedCode) {
            setAccessCode(storedCode);
        }

        const storedWhatsAppData = readLocalStorage(STORAGE_KEY_DATA);
        if (storedWhatsAppData) {
            try {
                setWhatsAppData(JSON.parse(storedWhatsAppData));
            } catch {
                // do nothing
            }
        }
    }, []);

    React.useEffect(() => {
        if (accessCode) {
            writeLocalStorage(STORAGE_KEY_CODE, accessCode);
        }
    }, [accessCode]);

    // Update localStorage when whatsappData changes
    React.useEffect(() => {
        if (whatsAppData) {
            writeLocalStorage(STORAGE_KEY_DATA, JSON.stringify(whatsAppData));
        }
    }, [whatsAppData]);

    // If both code and WhatsApp data are available, make the fetch
    React.useEffect(() => {
        if (accessCode && whatsAppData) {
            fetch('/facebook/whatsapp-oauth', {
                method: 'POST',
                body: JSON.stringify({code: accessCode, data: whatsAppData}),
            })
                .then((response) => {
                    if (!response.ok) {
                        return response
                            .json()
                            .then((data) => {
                                throw new Error(data.error || 'Failed to connect WhatsApp');
                            })
                            .catch((e) => {
                                // If it's not valid JSON, fall back to text
                                if (e instanceof SyntaxError) {
                                    return response.text().then((text) => {
                                        throw new Error(`Failed to connect WhatsApp: ${text}`);
                                    });
                                }
                                throw e;
                            });
                    }
                    // Clear storage after API call
                    removeLocalStorageItem(STORAGE_KEY_CODE);
                    removeLocalStorageItem(STORAGE_KEY_DATA);

                    // Reset state
                    setAccessCode(null);
                    setWhatsAppData(null);

                    refetchWhatsAppIntegration();
                })
                .catch((error) => {
                    setErrorMessage(error.message || 'Failed to connect WhatsApp');
                    // Clear storage when there's an error
                    removeLocalStorageItem(STORAGE_KEY_CODE);
                    removeLocalStorageItem(STORAGE_KEY_DATA);
                    setAccessCode(null);
                    setWhatsAppData(null);
                });
        }
    }, [accessCode, whatsAppData, refetchWhatsAppIntegration, setErrorMessage]);

    React.useEffect(() => {
        // Load the Facebook SDK script
        const script = document.createElement('script');
        script.src = 'https://connect.facebook.net/en_US/sdk.js';
        script.async = true;
        script.defer = true;
        script.crossOrigin = 'anonymous';
        script.onload = () => {
            // Initialize Facebook SDK
            // @ts-expect-error
            if (window.FB) {
                // @ts-expect-error
                window.FB.init({
                    appId: NutClientConfig.facebookSdkAppId,
                    autoLogAppEvents: true,
                    xfbml: true,
                    version: 'v22.0',
                });
            }
        };

        window.addEventListener('message', (event) => {
            if (
                event.origin !== 'https://www.facebook.com' &&
                event.origin !== 'https://web.facebook.com'
            ) {
                return;
            }

            try {
                const data = JSON.parse(event.data);
                if (data.type === 'WA_EMBEDDED_SIGNUP') {
                    // if user finishes the Embedded Signup flow with an existing Business account
                    if (
                        data.event === 'FINISH' ||
                        data.event === 'FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING'
                    ) {
                        if (data.data) {
                            setWhatsAppData(data.data);
                        }
                        // if user cancels the Embedded Signup flow
                    } else if (data.event === 'CANCEL') {
                        // if user reports an error during the Embedded Signup flow
                    } else if (data.event === 'ERROR') {
                        // do nothing
                    }
                }
            } catch (_error: any) {
                // do nothing
            }
        });

        if (document.body) {
            document.body.appendChild(script);
        } else if (document.head) {
            document.head.appendChild(script);
        }

        return () => {
            if (script.parentNode) {
                script.parentNode.removeChild(script);
            }
        };
    }, [setErrorMessage]);

    // once we know we are successfully connected, we can auto advance
    React.useEffect(() => {
        if (props.isConnected) {
            setPageAutoAdvance(true);
        }
    }, [props.isConnected]);

    // @ts-expect-error
    const fbLoginCallback = (response) => {
        if (response.authResponse) {
            const code = response.authResponse.code;
            setAccessCode(code);
        } else if (response.error) {
            setErrorMessage(response.error || 'Authentication failed');
        }
    };

    const launchWhatsAppSignup = () => {
        setPageAutoAdvance(false);

        // Launch Facebook login
        // @ts-expect-error
        if (window.FB) {
            // @ts-expect-error
            window.FB.login(fbLoginCallback, {
                config_id: NutClientConfig.facebookSdkConfigId, // configuration ID goes here
                response_type: 'code', // must be set to 'code' for System User access token
                override_default_response_type: true, // when true, any response types passed in the "response_type" will take precedence over the default types
                extras: {
                    setup: {},
                    featureType: 'whatsapp_business_app_onboarding',
                    sessionInfoVersion: '3',
                },
            });
        }
    };

    const initiateSync = (shouldSyncContacts: boolean, shouldSyncMessages: boolean) => {
        initiateWhatsAppSync({
            shouldSyncContacts,
            shouldSyncMessages,
        });
    };

    return (
        <OnboardingModal
            isConnected={props.isConnected}
            isEnabled={props.isEnabled}
            connectIntegration={launchWhatsAppSignup}
            enableIntegration={props.enableIntegration}
            disableIntegration={props.disableIntegration}
            isLoadingConnection={Boolean(whatsAppData && !props.isConnected)}
            onClose={() => props.setIsOnboardingModalOpen(false)}
            errorMessage={props.errorMessage}
            setErrorMessage={setErrorMessage}
            pageAutoAdvance={pageAutoAdvance}
            whatsappPhoneNumber={props.whatsappPhoneNumber}
            initiateSync={initiateSync}
        />
    );
}
